import type { ReactNode } from 'react'
import { Link } from '@/ui/Link/Link'
import { Container } from '@/ui/layout/Container'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { Wordmark } from '@/ui/Wordmark/Wordmark'
import styles from './AuthShell.module.css'
import { useDocumentTitle } from './useDocumentTitle'

/**
 * Shared frame for the unauthenticated pages (login / forgot / reset /
 * accept-invite): the wordmark, an optional page title, then the page's form
 * in a centred form-width column. It renders the route's one `<main>`; the
 * `<h1>` is the page title, or the wordmark when there is none.
 */
export function AuthShell({
  heading,
  children,
}: {
  heading?: string
  children: ReactNode
}) {
  useDocumentTitle(heading ?? '')
  return (
    <main data-surface="page" className={styles.room}>
      <Container size="container-form">
        <Stack gap="space-24">
          <Stack gap="space-24" className={styles.masthead}>
            <Wordmark as={heading ? 'p' : 'h1'} />
            {heading && (
              <Text as="h1" variant="heading">
                {heading}
              </Text>
            )}
          </Stack>
          {children}
        </Stack>
      </Container>
    </main>
  )
}

/** The link line under an auth form: one centred `ui` line holding one link. */
export function AuthLink({
  href,
  children,
}: {
  href: string
  children: ReactNode
}) {
  return (
    <Text as="p" className={styles.linkLine}>
      <Link href={href}>{children}</Link>
    </Text>
  )
}
